import React from 'react';
import {
  BookOpen,
  Brain,
  Briefcase,
  Calculator,
  Code2,
  Cpu,
  FileText,
  Folder,
  Gamepad2,
  Grid3X3,
  HardDrive,
  HelpCircle,
  Image,
  Info,
  LucideIcon,
  MapPin,
  Monitor,
  Music,
  Orbit,
  Palette,
  Settings,
  Terminal,
  Trash2,
  Wifi,
} from 'lucide-react';
import { cn } from '../lib/utils';

type FluencyIconProps = {
  size?: number;
  className?: string;
  alt?: string;
};

type LocalIconTheme = {
  glyph: LucideIcon;
  from: string;
  to: string;
  glow: string;
  glyphClassName?: string;
  corner?: string;
};

const THEMES: Record<string, LocalIconTheme> = {
  document: {
    glyph: FileText,
    from: '#5b8def',
    to: '#6f5ef9',
    glow: 'rgba(91,141,239,0.35)',
  },
  folder: {
    glyph: Folder,
    from: '#f8c34d',
    to: '#f59e0b',
    glow: 'rgba(245,158,11,0.35)',
    glyphClassName: 'text-[#fff9ec]',
  },
  briefcase: {
    glyph: Briefcase,
    from: '#8b5cf6',
    to: '#ec4899',
    glow: 'rgba(168,85,247,0.35)',
  },
  settings: {
    glyph: Settings,
    from: '#38bdf8',
    to: '#2563eb',
    glow: 'rgba(59,130,246,0.35)',
  },
  monitor: {
    glyph: Monitor,
    from: '#34d399',
    to: '#0ea5a4',
    glow: 'rgba(16,185,129,0.35)',
  },
  'recycle-bin': {
    glyph: Trash2,
    from: '#94a3b8',
    to: '#475569',
    glow: 'rgba(100,116,139,0.35)',
  },
  wifi: {
    glyph: Wifi,
    from: '#22c55e',
    to: '#14b8a6',
    glow: 'rgba(34,197,94,0.35)',
  },
  info: {
    glyph: Info,
    from: '#60a5fa',
    to: '#3b82f6',
    glow: 'rgba(59,130,246,0.35)',
  },
  help: {
    glyph: HelpCircle,
    from: '#f472b6',
    to: '#a855f7',
    glow: 'rgba(236,72,153,0.35)',
  },
  'artificial-intelligence': {
    glyph: Brain,
    from: '#c084fc',
    to: '#7c3aed',
    glow: 'rgba(168,85,247,0.38)',
  },
  engine: {
    glyph: Cpu,
    from: '#fb7185',
    to: '#f97316',
    glow: 'rgba(249,115,22,0.36)',
  },
  grid: {
    glyph: Grid3X3,
    from: '#60a5fa',
    to: '#8b5cf6',
    glow: 'rgba(99,102,241,0.35)',
  },
  console: {
    glyph: Terminal,
    from: '#111827',
    to: '#374151',
    glow: 'rgba(17,24,39,0.35)',
  },
  'hard-drive': {
    glyph: HardDrive,
    from: '#64748b',
    to: '#334155',
    glow: 'rgba(71,85,105,0.35)',
  },
  'source-code': {
    glyph: Code2,
    from: '#2563eb',
    to: '#1d4ed8',
    glow: 'rgba(37,99,235,0.35)',
  },
  'paint-palette': {
    glyph: Palette,
    from: '#fb7185',
    to: '#f59e0b',
    glow: 'rgba(244,114,182,0.35)',
  },
  controller: {
    glyph: Gamepad2,
    from: '#22c55e',
    to: '#14b8a6',
    glow: 'rgba(20,184,166,0.35)',
  },
  image: {
    glyph: Image,
    from: '#38bdf8',
    to: '#22c55e',
    glow: 'rgba(34,197,94,0.35)',
  },
  music: {
    glyph: Music,
    from: '#ec4899',
    to: '#8b5cf6',
    glow: 'rgba(168,85,247,0.35)',
  },
  calculator: {
    glyph: Calculator,
    from: '#14b8a6',
    to: '#0f766e',
    glow: 'rgba(20,184,166,0.35)',
  },
  'spreadsheet-file': {
    glyph: FileText,
    from: '#22c55e',
    to: '#16a34a',
    glow: 'rgba(34,197,94,0.35)',
  },
  marker: {
    glyph: MapPin,
    from: '#ef4444',
    to: '#f97316',
    glow: 'rgba(239,68,68,0.35)',
  },
  satellite: {
    glyph: Orbit,
    from: '#06b6d4',
    to: '#6366f1',
    glow: 'rgba(99,102,241,0.35)',
  },
  book: {
    glyph: BookOpen,
    from: '#f59e0b',
    to: '#ef4444',
    glow: 'rgba(245,158,11,0.35)',
  },
};

function LocalFluencyIcon({
  theme,
  size = 18,
  className,
  alt,
}: FluencyIconProps & { theme: LocalIconTheme }) {
  const Glyph = theme.glyph;
  const radius = Math.max(6, Math.round(size * 0.28));
  const glyphSize = Math.max(10, Math.round(size * 0.5));

  return (
    <span
      role="img"
      aria-label={alt}
      className={cn('relative inline-flex shrink-0 select-none', className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        className="absolute inset-0"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={`grad-${theme.from}-${theme.to}`.replace(/[^a-zA-Z0-9]/g, '')} x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={theme.from} />
            <stop offset="100%" stopColor={theme.to} />
          </linearGradient>
          <radialGradient id={`shine-${theme.from}-${theme.to}`.replace(/[^a-zA-Z0-9]/g, '')} cx="22" cy="16" r="30" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="rgba(255,255,255,0.85)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
        </defs>
        <rect x="4" y="6" width="56" height="56" rx={radius} fill={theme.glow} opacity="0.55" />
        <rect
          x="6"
          y="4"
          width="52"
          height="52"
          rx={radius}
          fill={`url(#${`grad-${theme.from}-${theme.to}`.replace(/[^a-zA-Z0-9]/g, '')})`}
        />
        <path
          d="M12 14c0-4.418 3.582-8 8-8h28c4.418 0 8 3.582 8 8v3H12z"
          fill={`url(#${`shine-${theme.from}-${theme.to}`.replace(/[^a-zA-Z0-9]/g, '')})`}
          opacity="0.55"
        />
        <rect x="6" y="4" width="52" height="52" rx={radius} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center">
        <Glyph
          size={glyphSize}
          strokeWidth={2.25}
          className={cn('drop-shadow-[0_1px_2px_rgba(0,0,0,0.22)] text-white', theme.glyphClassName)}
        />
      </span>
    </span>
  );
}

export function makeFluencyIcon(slug: string, alt: string = slug) {
  const theme = THEMES[slug] || THEMES.document;

  const Icon: React.FC<FluencyIconProps> = ({ size = 18, className }) => {
    return <LocalFluencyIcon theme={theme} size={size} className={className} alt={alt} />;
  };

  Icon.displayName = `FluencyIcon(${slug})`;
  return Icon;
}
